/** Istio TCP exposure for database environments (database-manager /istio/db-gateway/*). */

export interface IRemediation {
  repo: string;
  file: string;
  instructions: string;
  suggested_yaml: {
    service_ports: string;
    gateway_servers: string;
  };
}

export interface IPortUsage {
  port: number;
  virtual_service: string;
  namespace: string;
  destination?: string | null;
  env_id?: number | null;
  /** False for hand-made VirtualServices: shown, but never acted on from here. */
  managed: boolean;
  /** False when another VirtualService claimed this port first — it carries no traffic. */
  serving?: boolean;
}

export interface IPortCapacity {
  gateway: string;
  address?: string | null;
  rbac_ok: boolean;
  rbac_error?: string | null;
  declared: number[];
  declared_count: number;
  used: IPortUsage[];
  used_count: number;
  free: number[];
  free_count: number;
  /** Free ports never allocated before; preferred over recycled ones. */
  fresh: number[];
  fresh_count: number;
  recycled: number[];
  /** Held by an in-flight allocation. Excluded from `free`. */
  reserved: number[];
  /** `free` ignoring in-flight reservations — what the range holds once contention clears. */
  unreserved: number[];
  /** Authoritative: only `exhausted` means the range needs extending. */
  exhausted: boolean;
  /** Ports exist but are all momentarily reserved. Clears on its own. */
  contended: boolean;
  watermark: number;
  next_port?: number | null;
  low_watermark: boolean;
  gateway_only: number[];
  service_only: number[];
  undeclared_used: number[];
  /** Objects that exist but lost their port claim: they occupy no port, so they are not in
   *  `used`. Optional so an older backend degrades rather than breaking. */
  shadowed?: IPortUsage[];
  remediation?: IRemediation | null;
}

export interface IExposure {
  /** False when another object claimed the port first: it exists but routes nothing. */
  serving?: boolean;
  env_id?: number | null;
  name: string;
  namespace: string;
  port: number;
  destination?: string | null;
  gateway: string;
  managed: boolean;
  connection_hint?: string | null;
}

export type PreflightReason =
  /** Client-side only: the capacity check itself failed, so intent must be confirmed. */
  | "capacity_unknown"
  | "ports_exhausted"
  | "ports_contended"
  | "already_exposed"
  | "name_taken"
  | "invalid_name"
  | "rbac_denied";

export interface IPreflight {
  can_expose: boolean;
  gateway?: string | null;
  reason?: PreflightReason | null;
  message?: string | null;
  name?: string | null;
  next_port?: number | null;
  connection_hint?: string | null;
  free_count: number;
  fresh_count: number;
  declared_count: number;
  low_watermark: boolean;
  existing?: IExposure | null;
  remediation?: IRemediation | null;
}

export type ExposureState =
  | "exposed"
  | "exposed_unmanaged"
  | "not_exposed"
  | "not_exposed_no_ports";

/** Mirrors the server rule; the server revalidates, this only avoids a round trip. */
export const EXPOSURE_NAME_PREFIX = "tsdb-internal-";
export const MAX_EXPOSURE_NAME_LENGTH = 63 - EXPOSURE_NAME_PREFIX.length;
/** The API caps storage/memory/cpu at 6 characters (DBEnvironment max_length). */
export const MAX_QUANTITY_LENGTH = 6;
const RFC1123_LABEL = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

export const slugifyExposureName = (value: string): string =>
  (value || "")
    .normalize("NFKD")
    // eslint-disable-next-line no-control-regex
    .replace(/[̀-ͯ]/g, "")
    // The server does encode("ascii", "ignore"), which DROPS what NFKD could not fold
    // rather than turning it into a separator: "Eco•Martinez" is ecomartinez, not
    // eco-martinez. Dropping here too keeps the proposed name and the derived one equal.
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\x7F]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_EXPOSURE_NAME_LENGTH)
    .replace(/-+$/g, "");

/**
 * Default exposure name, matching the server's `build_default_slug`.
 *
 * Each part is slugified *before* joining, as the server does, and a part already present is
 * dropped: tenant and environment usually carry the same label, which joined verbatim gives
 * "tsdb-internal-<x>-<x>". Slugifying after joining instead would also truncate differently
 * at the 63-character limit.
 */
export const buildDefaultExposureName = (...parts: (string | undefined | null)[]): string => {
  const kept: string[] = [];
  parts.forEach((part) => {
    const slug = slugifyExposureName(part || "");
    if (slug && !kept.includes(slug)) kept.push(slug);
  });
  return slugifyExposureName(kept.join("-"));
};

export const validateExposureName = (value: string): string | null => {
  if (!value) return "Exposure name must not be empty";
  if (value.length > MAX_EXPOSURE_NAME_LENGTH) {
    return `Exposure name must be at most ${MAX_EXPOSURE_NAME_LENGTH} characters`;
  }
  if (!RFC1123_LABEL.test(value)) {
    return "Use lowercase letters, digits and '-', starting and ending with a letter or digit";
  }
  return null;
};

/** Every environment lands in exactly one of these. Only `missing` is ever acted on. */
export type ReconcileBucket =
  | "in_sync"
  | "missing"
  | "unrecorded"
  | "port_mismatch"
  | "name_mismatch"
  | "duplicate"
  | "port_shadowed"
  | "port_taken"
  | "no_database_service"
  | "port_undeclared";

export interface IReconcileEntry {
  env_id: number;
  bucket: ReconcileBucket;
  recorded_port?: number | null;
  recorded_name?: string | null;
  recorded_at?: string | null;
  recorded_managed?: boolean | null;
  live_port?: number | null;
  live_name?: string | null;
  live_managed?: boolean | null;
  note?: string | null;
}

export interface IUnidentifiedExposure {
  port: number;
  virtual_service: string;
  namespace?: string | null;
}

export interface IRecordFreshness {
  /** The only field that stays true after a restore: it comes from the restored data. */
  newest_exposure_at?: string | null;
  blind_window_seconds: number;
  /** Live-cluster health only. After a rebuild it describes the NEW cluster. */
  backup_last_success_at?: string | null;
  backup_note?: string | null;
  /** Null when the plan could not be computed — not known, as distinct from none recorded. */
  recorded_count?: number | null;
}

export interface IReconcilePlan {
  gateway: string;
  /** Reported rather than thrown, like the ports endpoint, so controls can be disabled. */
  rbac_ok: boolean;
  rbac_error?: string | null;
  counts: Record<ReconcileBucket, number>;
  entries: IReconcileEntry[];
  unidentified: IUnidentifiedExposure[];
  record_freshness: IRecordFreshness;
  actionable: number;
  adoptable: number;
}

export interface IReconcileResult {
  requested: number[];
  applied: { env_id: number; port: number; name: string }[];
  adopted: { env_id: number; port: number; name: string; managed: boolean; was: string }[];
  failed: { env_id: number; error?: string; code?: string }[];
  /** Ids asked for that were not in the bucket, so "0 rebuilt" is never unexplained.
   *  Optional: a backend older than this field simply omits it. */
  skipped?: { env_id: number; reason: string }[];
  counts: Record<string, number>;
}

export const BUCKET_LABEL: Record<ReconcileBucket, string> = {
  in_sync: "In sync",
  missing: "Missing — can be rebuilt",
  unrecorded: "Not recorded",
  port_mismatch: "Record is stale",
  name_mismatch: "Recorded name is stale",
  duplicate: "More than one exposure",
  port_shadowed: "Exposure carries no traffic",
  port_taken: "Recorded port taken",
  no_database_service: "Database not restored",
  port_undeclared: "Recorded port undeclared",
};

export const BUCKET_HINT: Record<ReconcileBucket, string> = {
  in_sync: "The recorded port matches what is live. Nothing to do.",
  missing: "Recorded, with a database and a declared port, but no VirtualService. Apply rebuilds it on its recorded port and name.",
  unrecorded: "A VirtualService exists that nothing has recorded. Adopt records it so a cluster loss can rebuild it.",
  port_mismatch: "Live on a different port than the record says. The live port is never moved; adopt re-records it.",
  name_mismatch: "The port matches, so traffic is fine — but a rebuild uses the recorded name, which would rename the object. Adopt re-records it.",
  duplicate: "This environment has more than one VirtualService on the gateway. Which one is authoritative is a human call, so it is never recorded or rebuilt — remove the extra object first.",
  port_shadowed: "The VirtualService exists but another object claimed its port first, so Istio never routes to it. It is not missing — rebuilding would leave two objects — so move it to a free port or remove it.",
  port_taken: "The recorded port now serves a different environment. Needs a human.",
  no_database_service: "Recorded, with no VirtualService and no database Service — the database has not been restored yet. Note this keys on the Service existing, not on the pods running: an environment scaled to zero keeps its Service and stays in sync.",
  port_undeclared: "The recorded port is no longer declared on the gateway, so rebuilding it would carry no traffic.",
};
